import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';

import { AgGridModule } from 'ag-grid-angular';
import { ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';

import { BaasAppState, BaasToepassingService } from '@lvflanders/bar';
import { BarCoreService } from '@lvflanders/bar-ng';
import { BAR_AG_GRID_LOCALE, themeLv } from '@lvflanders/bar-ng-ag-grid';
import { BaasTileList, BaasTileOptions, BarI18nPipe } from '@lvflanders/bar-ng-ui';

import { VoorwiepickerDto } from '../../../api/models';
import { VoorwiepickerDialogData } from '../../../models/v2/voorwiepicker-dialog-data.type';
import { StartschermV2FormatteerInternalService } from '../../../services/v2/startscherm-v2-formatteer-internal.service';
import { VoorwiepickerApiService } from './../../../api/services/voorwiepicker-api.service';

@Component({
    selector: 'startscherm-voorwie-dialog',
    templateUrl: './startscherm-voorwie-dialog.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatDialogTitle, MatDialogContent, AgGridModule, BaasTileList, MatDialogActions, MatButton, MatDialogClose, BarI18nPipe]
})
export class StartschermVoorwieDialog implements OnInit {
    protected readonly data = inject<VoorwiepickerDialogData>(MAT_DIALOG_DATA);
    readonly #toepassing = inject(BaasToepassingService);
    readonly #dialogRef = inject(MatDialogRef<StartschermVoorwieDialog>);
    readonly #voorwiepickerApiService = inject(VoorwiepickerApiService);
    readonly #startschermV2FormatteerInternalService = inject(StartschermV2FormatteerInternalService);
    readonly #formattingService = inject(StartschermV2FormatteerInternalService);
    readonly #core = inject(BarCoreService);
    readonly #userState = inject(BaasAppState);

    protected readonly clients = signal<VoorwiepickerDto[] | undefined>(undefined);
    protected readonly tileClients = computed(() => {
        if (!this.gridview()) {
            const clients = this.clients();
            if (!clients) return [];
            return clients.map((x, idx): BaasTileOptions => {
                return {
                    hideFavoriteIndicator: true,
                    headerLeft: x.rolcodeomschrijving,
                    content: `${x.naam}`,
                    body: x.adres,
                    footerLeft: x.identificatienummer ? x.identificatienummer : null,
                    footerLeftTooltip: x.omschrijvingIdentificatienummer,
                    action: () => this.clientSelected(x),
                    initiator: true
                };
            });
        }
        return [];
    });
    protected readonly gridview = computed(() => {
        const clients = this.clients();
        if (clients && clients.length <= 6) return false;
        return true;
    });
    // criteria to get 'paged' result set from backend
    protected columnDefs: ColDef<VoorwiepickerDto>[] = [];
    protected defaultColDef: ColDef | undefined;
    protected formattedKlantnummer: string | undefined;
    #gridApi: GridApi | undefined;

    // options for the ag-grid
    protected readonly gridOptions: GridOptions<VoorwiepickerDto> = {
        tooltipShowDelay: 200,
        localeText: BAR_AG_GRID_LOCALE,
        theme: themeLv
    };

    ngOnInit() {
        this.#initializeColDefs();

        this.formattedKlantnummer = this.#formattingService.formatteerKlantnummer(this.data.klantnummer);

        this.clients.set(this.data.clients);

        if (!this.clients()) {
            this.#voorwiepickerApiService.getVoorwies().subscribe((res) => {
                res = res.sort((x, y) => (x.naam ?? '').localeCompare(y.naam ?? ''));
                this.clients.set(res);
            });
        }
    }

    protected onGridReady(params: GridReadyEvent) {
        this.#gridApi = params.api;
        this.#gridApi.sizeColumnsToFit();
    }

    protected clientSelected(client: VoorwiepickerDto) {
        //Als er een hoedanigheid is, deze resetten om onbestaande combinaties te vermijden
        if (this.#userState.basiscontext.hoedanigheid) {
            this.#toepassing.resetHoedanigheid();
        }
        this.#toepassing.setVoorwie(client.klantnummer ?? '', client.rolcode ?? '').then(() => {
            if (this.data.noNavigation) {
                if (this.data.doReset) {
                    this.#dialogRef.close({ client: client, reset: this.data.doReset });
                } else {
                    this.#dialogRef.close({ client: client, reset: this.data.doReset });
                }
            } else {
                this.#core.navigateHome(false);
            }
        });
    }

    protected verwijderVoorwie() {
        this.#dialogRef.close({ reset: true });
    }

    #initializeColDefs() {
        this.defaultColDef = { filter: 'agTextColumnFilter', sortable: true, floatingFilter: true };
        this.columnDefs = [
            {
                headerName: 'Klantnummer',
                field: 'klantnummer',
                width: 200,
                valueFormatter: (rowdata) => this.#startschermV2FormatteerInternalService.formatteerKlantnummer(rowdata.value),
                filterParams: {
                    filterOptions: ['contains'],

                    textMatcher: ({ filterOption, value, filterText }) => {
                        if (filterText == null) {
                            return false;
                        }
                        switch (filterOption) {
                            case 'contains':
                                return value.includes(filterText.replace(/\./g, '').replace(/-/g, ''));
                            default:
                                // should never happen
                                return false;
                        }
                    }
                }
            },
            {
                headerName: 'Identificatienummer',
                field: 'identificatienummer',
                tooltipField: 'omschrijvingIdentificatienummer',
                width: 200,
                filterParams: {
                    filterOptions: ['contains'],
                    textMatcher: ({ filterOption, value, filterText }) => {
                        if (filterText == null) {
                            return false;
                        }
                        switch (filterOption) {
                            case 'contains':
                                return value.replace(/\./g, '').replace(/-/g, '').includes(filterText.replace(/\./g, '').replace(/-/g, ''));
                            default:
                                // should never happen
                                return false;
                        }
                    }
                }
            },
            { headerName: 'Naam', field: 'naam', width: 300 },
            { headerName: 'Adres', field: 'adres', width: 600 }
        ];
    }
}
